/**
 * Audio.
 *
 * The original Emoji Garden shipped ~40 WAV files whose names give the whole
 * game away: `sfx_eg_tap_type1_scale_1_C4_01` through `..._G5_12`. Every tap
 * played the next note up a scale, so growing a plant played a rising melody
 * and a garden full of players sounded like an ensemble rather than a mess.
 *
 * We reproduce that with an oscillator instead of shipping megabytes of audio.
 * Synthesis also buys something the samples could not: each species gets its
 * own tonal centre, so any combination of plants stays consonant.
 */

/** Major pentatonic, in semitones. No semitone clashes, so nothing can sound wrong. */
const PENTATONIC = [0, 2, 4, 7, 9, 12, 14, 16, 19, 21, 24];

function midiToFreq(midi: number): number {
  return 440 * Math.pow(2, (midi - 69) / 12);
}

export class AudioEngine {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private musicGain: GainNode | null = null;
  private padTimer: number | null = null;
  private _muted = false;

  get muted(): boolean {
    return this._muted;
  }

  /**
   * Must be called from inside a user gesture — every mobile browser
   * suspends audio contexts created outside one.
   */
  async unlock(): Promise<void> {
    if (!this.ctx) {
      const Ctor: typeof AudioContext =
        window.AudioContext ?? (window as any).webkitAudioContext;
      this.ctx = new Ctor();
      this.master = this.ctx.createGain();
      this.master.gain.value = 0.9;
      this.master.connect(this.ctx.destination);

      this.musicGain = this.ctx.createGain();
      this.musicGain.gain.value = 0.0;
      this.musicGain.connect(this.master);
    }
    if (this.ctx.state === 'suspended') await this.ctx.resume();
  }

  setMuted(muted: boolean): void {
    this._muted = muted;
    if (this.master && this.ctx) {
      this.master.gain.setTargetAtTime(muted ? 0 : 0.9, this.ctx.currentTime, 0.05);
    }
  }

  /**
   * A plucked note. `rootSemitone` is the species' tonal centre and `step`
   * climbs the pentatonic scale as the plant grows.
   */
  pluck(rootSemitone: number, step: number, velocity = 1): void {
    const ctx = this.ctx;
    if (!ctx || !this.master || this._muted) return;

    const midi = 60 + rootSemitone + PENTATONIC[Math.min(step, PENTATONIC.length - 1)];
    const freq = midiToFreq(midi);
    const now = ctx.currentTime;

    // Two detuned voices — a triangle body and a sine sub — give the note
    // some weight without needing a filter sweep.
    const gain = ctx.createGain();
    gain.connect(this.master);
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(0.26 * velocity, now + 0.008);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 1.1);

    const body = ctx.createOscillator();
    body.type = 'triangle';
    body.frequency.setValueAtTime(freq, now);
    body.connect(gain);

    const sub = ctx.createOscillator();
    sub.type = 'sine';
    sub.frequency.setValueAtTime(freq / 2, now);
    const subGain = ctx.createGain();
    subGain.gain.value = 0.45;
    sub.connect(subGain).connect(gain);

    // A touch of shimmer an octave and a fifth up, decaying fast — this is
    // what makes it read as "plant" rather than "beep".
    const bell = ctx.createOscillator();
    bell.type = 'sine';
    bell.frequency.setValueAtTime(freq * 3, now);
    const bellGain = ctx.createGain();
    bellGain.gain.setValueAtTime(0.09 * velocity, now);
    bellGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.28);
    bell.connect(bellGain).connect(this.master);

    body.start(now);
    sub.start(now);
    bell.start(now);
    body.stop(now + 1.15);
    sub.stop(now + 1.15);
    bell.stop(now + 0.3);
  }

  /** The flourish when a plant reaches full growth — an arpeggio up the scale. */
  bloom(rootSemitone: number): void {
    if (!this.ctx || this._muted) return;
    [0, 2, 4, 5].forEach((step, i) => {
      window.setTimeout(() => this.pluck(rootSemitone, step + 4, 0.75), i * 85);
    });
  }

  /** Low thud for placing the plot. */
  thud(): void {
    const ctx = this.ctx;
    if (!ctx || !this.master || this._muted) return;
    const now = ctx.currentTime;

    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(160, now);
    osc.frequency.exponentialRampToValueAtTime(48, now + 0.28);

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.5, now);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.4);

    osc.connect(gain).connect(this.master);
    osc.start(now);
    osc.stop(now + 0.45);
  }

  /** Soft tick for UI presses. */
  tick(): void {
    const ctx = this.ctx;
    if (!ctx || !this.master || this._muted) return;
    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(880, now);
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.07, now);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.08);
    osc.connect(gain).connect(this.master);
    osc.start(now);
    osc.stop(now + 0.1);
  }

  /**
   * A slow ambient pad that drifts under the whole experience. Deliberately
   * quiet — this plays outdoors, over a live microphone-adjacent camera feed,
   * and should never compete with the world.
   */
  startAmbience(): void {
    const ctx = this.ctx;
    if (!ctx || !this.musicGain || this.padTimer !== null) return;

    this.musicGain.gain.setTargetAtTime(0.11, ctx.currentTime, 2);

    const chord = [0, 7, 12, 16]; // open fifth plus a tenth: warm, unresolved
    const playPad = () => {
      if (!this.ctx || !this.musicGain) return;
      const t = this.ctx.currentTime;
      chord.forEach((semi, i) => {
        const osc = this.ctx!.createOscillator();
        osc.type = 'sine';
        osc.frequency.value = midiToFreq(48 + semi);
        osc.detune.value = (i - 1.5) * 6;

        const g = this.ctx!.createGain();
        g.gain.setValueAtTime(0, t);
        g.gain.linearRampToValueAtTime(0.12, t + 3);
        g.gain.linearRampToValueAtTime(0, t + 9);

        osc.connect(g).connect(this.musicGain!);
        osc.start(t);
        osc.stop(t + 9.5);
      });
    };

    playPad();
    this.padTimer = window.setInterval(playPad, 8500);
  }

  stopAmbience(): void {
    if (this.padTimer !== null) {
      window.clearInterval(this.padTimer);
      this.padTimer = null;
    }
    if (this.musicGain && this.ctx) {
      this.musicGain.gain.setTargetAtTime(0, this.ctx.currentTime, 0.5);
    }
  }
}
